import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    CheckCircleIcon, ExclamationTriangleIcon, UserGroupIcon,
    CalendarDaysIcon, DocumentTextIcon, LinkIcon,
    ClipboardDocumentListIcon, TrashIcon, PlusIcon, ArrowLeftIcon,
} from "@heroicons/react/24/outline";
import Spinner from "../components/Spinner";

interface Participant { id: number; nom: string; email: string; }
interface Template { id: number; nom: string; }
interface FormulaireReunion {
    titre: string;
    ordresduJour: string[];
    date: string;
    participantsIds: number[];
    templateId: number | null;
    lienMeet: string;
}

const dateMinLocale = (): string => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
};

const initialesParticipant = (nom: string): string => {
    const parties = nom.trim().split(" ");
    if (parties.length >= 2) return (parties[0][0] + parties[1][0]).toUpperCase();
    return nom.substring(0, 2).toUpperCase();
};

const SectionHeader = ({ icon, titre, required }: { icon: React.ReactNode; titre: string; required?: boolean }) => (
    <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white"
            style={{ background: "linear-gradient(135deg, #6B21A8, #F97316)" }}>
            {icon}
        </div>
        <span className="font-semibold text-gray-700 text-sm">{titre}</span>
        {required && <span className="text-red-400 text-xs">*</span>}
    </div>
);

export default function NouvelleReunion() {
    const [formulaire, setFormulaire] = useState<FormulaireReunion>({
        titre: "", ordresduJour: [""], date: "",
        participantsIds: [], templateId: null, lienMeet: "",
    });
    const [participants, setParticipants] = useState<Participant[]>([]);
    const [templates, setTemplates] = useState<Template[]>([]);
    const [chargement, setChargement] = useState(false);
    const [chargementDonnees, setChargementDonnees] = useState(true);
    const [erreur, setErreur] = useState("");
    const [succes, setSucces] = useState(false);
    const naviguer = useNavigate();

    useEffect(() => {
        const recupererDonnees = async () => {
            try {
                const [repParticipants, repTemplates] = await Promise.all([
                    fetch(`${process.env.REACT_APP_API_URL}/api/utilisateurs`,
                        { credentials: "include", headers: { "Content-Type": "application/json" } }),
                    fetch(`${process.env.REACT_APP_API_URL}/api/templates`,
                        { credentials: "include", headers: { "Content-Type": "application/json" } }),
                ]);
                if (!repParticipants.ok || !repTemplates.ok) throw new Error();
                setParticipants(await repParticipants.json());
                setTemplates(await repTemplates.json());
            } catch {
                setErreur("Impossible de charger les données");
            } finally {
                setChargementDonnees(false);
            }
        };
        recupererDonnees();
    }, []);

    const mettreAJourChamp = (champ: keyof FormulaireReunion, valeur: any) =>
        setFormulaire(p => ({ ...p, [champ]: valeur }));

    const mettreAJourODJ = (index: number, valeur: string) => {
        const n = [...formulaire.ordresduJour];
        n[index] = valeur;
        mettreAJourChamp("ordresduJour", n);
    };

    const ajouterODJ = () =>
        mettreAJourChamp("ordresduJour", [...formulaire.ordresduJour, ""]);

    const supprimerODJ = (index: number) => {
        if (formulaire.ordresduJour.length === 1) return;
        mettreAJourChamp("ordresduJour", formulaire.ordresduJour.filter((_, i) => i !== index));
    };

    const toggleParticipant = (id: number) => {
        const dejaSel = formulaire.participantsIds.includes(id);
        mettreAJourChamp("participantsIds", dejaSel
            ? formulaire.participantsIds.filter(p => p !== id)
            : [...formulaire.participantsIds, id]);
    };

    const validerFormulaire = (): boolean => {
        if (!formulaire.titre.trim()) { setErreur("Le titre est obligatoire"); return false; }
        if (!formulaire.date) { setErreur("La date et l'heure sont obligatoires"); return false; }
        if (new Date(formulaire.date) <= new Date()) { setErreur("La date doit être dans le futur"); return false; }
        if (formulaire.participantsIds.length === 0) { setErreur("Sélectionnez au moins un participant"); return false; }
        if (!formulaire.templateId) {
            setErreur(templates.length === 0 ? "Aucun template disponible — créez-en un d'abord" : "Sélectionnez un template");
            return false;
        }
        if (!formulaire.ordresduJour.some(o => o.trim())) { setErreur("Ajoutez au moins un point à l'ODJ"); return false; }
        return true;
    };

    const gererSoumission = async () => {
        setErreur("");
        if (!validerFormulaire()) return;
        setChargement(true);
        try {
            const reponse = await fetch(`${process.env.REACT_APP_API_URL}/api/reunions`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    ...formulaire,
                    ordresduJour: formulaire.ordresduJour.filter(o => o.trim()),
                }),
            });
            if (!reponse.ok) {
                const err = await reponse.json().catch(() => ({}));
                throw new Error(err.message || "Erreur lors de la création");
            }
            setSucces(true);
            setTimeout(() => naviguer("/tableau"), 2000);
        } catch (e: any) {
            setErreur(e.message || "Impossible de créer la réunion");
        } finally {
            setChargement(false);
        }
    };

    const participantsSelectionnes = participants.filter(p =>
        formulaire.participantsIds.includes(p.id));

    if (chargementDonnees) return (
        <div className="flex justify-center items-center h-64">
            <Spinner size="lg" className="text-purple-600" />
        </div>
    );

    return (
        <div className="max-w-3xl space-y-5">
            <div className="flex items-center gap-4">
                <button onClick={() => naviguer("/tableau")}
                    className="w-9 h-9 rounded-xl border border-gray-200 flex items-center justify-center text-gray-400 hover:bg-gray-50 transition-colors">
                    <ArrowLeftIcon className="w-4 h-4" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Nouvelle réunion</h1>
                    <p className="text-gray-400 text-sm">Planifiez et configurez votre réunion</p>
                </div>
            </div>

            {erreur && (
                <div className="alert alert-error rounded-xl text-sm">
                    <ExclamationTriangleIcon className="w-5 h-5 shrink-0" />
                    <span>{erreur}</span>
                </div>
            )}
            {succes && (
                <div className="alert alert-success rounded-xl text-sm">
                    <CheckCircleIcon className="w-5 h-5 shrink-0" />
                    <span>Réunion créée avec succès ! Redirection...</span>
                </div>
            )}

            <div className="grid grid-cols-1 gap-4">

                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                    <SectionHeader icon={<DocumentTextIcon className="w-4 h-4" />} titre="Informations générales" required />
                    <div className="space-y-4">
                        <div>
                            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block">
                                Titre de la réunion
                            </label>
                            <input type="text" placeholder="Ex: Stand-up hebdomadaire New Techs"
                                className="input input-bordered w-full rounded-xl border-2 focus:border-purple-500 focus:outline-none transition-all"
                                value={formulaire.titre}
                                onChange={e => mettreAJourChamp("titre", e.target.value)}
                                disabled={chargement} autoFocus />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block">
                                Date et heure
                            </label>
                            <input type="datetime-local" min={dateMinLocale()}
                                className="input input-bordered w-full rounded-xl border-2 focus:border-purple-500 focus:outline-none transition-all"
                                value={formulaire.date}
                                onChange={e => mettreAJourChamp("date", e.target.value)}
                                disabled={chargement} />
                            <p className="text-xs text-gray-400 mt-1.5">La réunion doit être planifiée dans le futur</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                    <SectionHeader icon={<ClipboardDocumentListIcon className="w-4 h-4" />} titre="Ordres du jour" required />
                    <div className="space-y-2">
                        {formulaire.ordresduJour.map((odj, index) => (
                            <div key={index} className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold text-white shrink-0"
                                    style={{ background: "linear-gradient(135deg, #6B21A8, #F97316)" }}>
                                    {index + 1}
                                </div>
                                <input type="text"
                                    placeholder={`Point ${index + 1} de l'ordre du jour`}
                                    className="input input-bordered flex-1 rounded-xl border-2 focus:border-purple-500 focus:outline-none transition-all text-sm"
                                    value={odj}
                                    onChange={e => mettreAJourODJ(index, e.target.value)}
                                    disabled={chargement} />
                                <button type="button"
                                    className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-300 hover:text-red-400 hover:bg-red-50 transition-colors"
                                    onClick={() => supprimerODJ(index)}
                                    disabled={formulaire.ordresduJour.length === 1 || chargement}>
                                    <TrashIcon className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                    <button type="button"
                        className="mt-3 flex items-center gap-2 text-sm font-medium text-purple-600 hover:text-purple-800 transition-colors py-1"
                        onClick={ajouterODJ} disabled={chargement}>
                        <div className="w-5 h-5 rounded-full border-2 border-purple-400 flex items-center justify-center">
                            <PlusIcon className="w-3 h-3" />
                        </div>
                        Ajouter un point
                    </button>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                    <SectionHeader icon={<UserGroupIcon className="w-4 h-4" />} titre="Participants" required />

                    {participantsSelectionnes.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                            {participantsSelectionnes.map(p => (
                                <div key={p.id} className="flex items-center gap-1.5 bg-purple-50 border border-purple-200 text-purple-700 text-xs font-medium px-2.5 py-1.5 rounded-lg">
                                    <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold"
                                        style={{ background: "linear-gradient(135deg, #6B21A8, #F97316)" }}>
                                        {initialesParticipant(p.nom)}
                                    </div>
                                    {p.nom}
                                    <button onClick={() => toggleParticipant(p.id)} className="text-purple-400 hover:text-purple-700 ml-1">×</button>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="border-2 border-gray-100 rounded-xl max-h-44 overflow-y-auto">
                        {participants.length === 0 ? (
                            <p className="text-gray-400 text-sm text-center py-6">Aucun participant disponible</p>
                        ) : (
                            participants.map(participant => {
                                const estSel = formulaire.participantsIds.includes(participant.id);
                                return (
                                    <label key={participant.id}
                                        className={`flex items-center gap-3 cursor-pointer px-3 py-2.5 transition-colors border-b border-gray-50 last:border-0 ${estSel ? "bg-purple-50" : "hover:bg-gray-50"}`}>
                                        <input type="checkbox" className="checkbox checkbox-primary checkbox-sm"
                                            checked={estSel}
                                            onChange={() => toggleParticipant(participant.id)}
                                            disabled={chargement} />
                                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
                                            style={{ background: estSel ? "linear-gradient(135deg, #6B21A8, #F97316)" : "#E5E7EB", color: estSel ? "white" : "#9CA3AF" }}>
                                            {initialesParticipant(participant.nom)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-gray-800 text-sm truncate">{participant.nom}</p>
                                            <p className="text-xs text-gray-400 truncate">{participant.email}</p>
                                        </div>
                                        {estSel && <CheckCircleIcon className="w-4 h-4 text-purple-600 shrink-0" />}
                                    </label>
                                );
                            })
                        )}
                    </div>
                    <p className="text-xs mt-2" style={{ color: formulaire.participantsIds.length > 0 ? "#6B21A8" : "#9CA3AF" }}>
                        {formulaire.participantsIds.length} participant(s) sélectionné(s)
                    </p>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                    <SectionHeader icon={<DocumentTextIcon className="w-4 h-4" />} titre="Configuration" required />
                    <div className="space-y-4">
                        <div>
                            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block">
                                Template Word
                            </label>
                            <select className="select select-bordered w-full rounded-xl border-2 focus:border-purple-500 focus:outline-none transition-all"
                                value={formulaire.templateId ?? ""}
                                onChange={e => mettreAJourChamp("templateId", Number(e.target.value))}
                                disabled={chargement}>
                                <option value="">Sélectionnez un template</option>
                                {templates.map(t => <option key={t.id} value={t.id}>{t.nom}</option>)}
                            </select>
                            {templates.length === 0 && (
                                <div className="mt-2 p-3 bg-orange-50 border border-orange-100 rounded-xl flex items-start gap-2">
                                    <ExclamationTriangleIcon className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
                                    <p className="text-xs text-orange-700">
                                        Aucun template.{" "}
                                        <a href="/templates" className="font-semibold underline">Créer un template</a>
                                    </p>
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                                <LinkIcon className="w-3.5 h-3.5" />Lien Google Meet
                                <span className="text-gray-300 font-normal normal-case">— optionnel</span>
                            </label>
                            <input type="url" placeholder="https://meet.google.com/..."
                                className="input input-bordered w-full rounded-xl border-2 focus:border-purple-500 focus:outline-none transition-all"
                                value={formulaire.lienMeet}
                                onChange={e => mettreAJourChamp("lienMeet", e.target.value)}
                                disabled={chargement} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex gap-3 pb-6">
                <button className="flex-1 py-3 rounded-xl font-medium border-2 border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                    onClick={() => naviguer("/tableau")} disabled={chargement}>
                    Annuler
                </button>
                <button className="flex-1 py-3 rounded-xl text-white font-semibold transition-opacity hover:opacity-90 disabled:opacity-40 flex items-center justify-center gap-2"
                    style={{ background: "linear-gradient(90deg, #6B21A8, #F97316)" }}
                    onClick={gererSoumission} disabled={chargement}>
                    {chargement
                        ? <Spinner size="sm" />
                        : <><CheckCircleIcon className="w-5 h-5" />Créer la réunion</>}
                </button>
            </div>
        </div>
    );
}
